<!--
MIT License

Copyright (c) 2022 Cloudeep

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.

original repository location:
    https://github.com/cloudeep/vuejs-paginate-next
description:
    This is a "forked" version on the original cloudeep vuejs-paginate-next,
    with the aim of adding href's for seo purposes.
-->

<script>
export default {
    data() {
        return {
            innerValue: 1,
        };
    },
    props: {
        modelValue: {
            type: Number,
        },
        pageCount: {
            type: Number,
            required: true,
        },
        initialPage: {
            type: Number,
            default: 1,
        },
        forcePage: {
            type: Number,
        },
        clickHandler: {
            type: Function,
            default: () => {},
        },
        pageRange: {
            type: Number,
            default: 3,
        },
        marginPages: {
            type: Number,
            default: 1,
        },
        currentBaseUrl: {
            type: String,
            default: "/"
        },
        urlQueryVars: {
            type: String,
            default: ""
        },
        prevText: {
            type: String,
            default: "Prev",
        },
        nextText: {
            type: String,
            default: "Next",
        },
        breakViewText: {
            type: String,
            default: "…",
        },
        containerClass: {
            type: String,
            default: "pagination",
        },
        pageClass: {
            type: String,
            default: "page-item",
        },
        pageLinkClass: {
            type: String,
            default: "page-link",
        },
        prevClass: {
            type: String,
            default: "page-item",
        },
        prevLinkClass: {
            type: String,
            default: "page-link",
        },
        nextClass: {
            type: String,
            default: "page-item",
        },
        nextLinkClass: {
            type: String,
            default: "page-link",
        },
        breakViewClass: {
            type: String,
        },
        breakViewLinkClass: {
            type: String,
        },
        activeClass: {
            type: String,
            default: "active",
        },
        disabledClass: {
            type: String,
            default: "disabled",
        },
        noLiSurround: {
            type: Boolean,
            default: false,
        },
        firstLastButton: {
            type: Boolean,
            default: false,
        },
        firstButtonText: {
            type: String,
            default: "First",
        },
        lastButtonText: {
            type: String,
            default: "Last",
        },
        hidePrevNext: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        selected: {
            get: function () {
                return this.modelValue || this.innerValue;
            },
            set: function (newValue) {
                this.innerValue = newValue;
            },
        },
        pages: function () {
            let items = {};
            if (this.pageCount <= this.pageRange) {
                for (let index = 0; index < this.pageCount; index++) {
                    let page = {
                        index: index,
                        content: index + 1,
                        selected: index === this.selected - 1,
                    };
                    items[index] = page;
                }
            } else {
                const halfPageRange = Math.floor(this.pageRange / 2);

                let setPageItem = (index) => {
                    let page = {
                        index: index,
                        content: index + 1,
                        selected: index === this.selected - 1,
                    };

                    items[index] = page;
                };

                let setBreakView = (index) => {
                    let breakView = {
                        disabled: true,
                        breakView: true,
                    };

                    items[index] = breakView;
                };

                // 1st - loop thru low end of margin pages
                for (let i = 0; i < this.marginPages; i++) {
                    setPageItem(i);
                }

                // 2nd - loop thru selected range
                let selectedRangeLow = 0;
                if (this.selected - halfPageRange > 0) {
                    selectedRangeLow = this.selected - 1 - halfPageRange;
                }

                let selectedRangeHigh = selectedRangeLow + this.pageRange - 1;
                if (selectedRangeHigh >= this.pageCount) {
                    selectedRangeHigh = this.pageCount - 1;
                    selectedRangeLow = selectedRangeHigh - this.pageRange + 1;
                }

                for (
                    let i = selectedRangeLow;
                    i <= selectedRangeHigh && i <= this.pageCount - 1;
                    i++
                ) {
                    setPageItem(i);
                }

                // Check if there is breakView in the left of selected range
                if (selectedRangeLow > this.marginPages) {
                    setBreakView(selectedRangeLow - 1);
                }

                // Check if there is breakView in the right of selected range
                if (selectedRangeHigh + 1 < this.pageCount - this.marginPages) {
                    setBreakView(selectedRangeHigh + 1);
                }

                // 3rd - loop thru high end of margin pages
                for (
                    let i = this.pageCount - 1;
                    i >= this.pageCount - this.marginPages;
                    i--
                ) {
                    setPageItem(i);
                }
            }
            return items;
        },
    },
    methods: {
        handlePageSelected(selected) {
            if (this.selected === selected) return;

            this.innerValue = selected;
            this.$emit("update:modelValue", selected);
            this.clickHandler(selected);
        },
        prevPage() {
            if (this.selected <= 1) return;

            this.handlePageSelected(this.selected - 1);
        },
        nextPage() {
            if (this.selected >= this.pageCount) return;

            this.handlePageSelected(this.selected + 1);
        },
        firstPageSelected() {
            return this.selected === 1;
        },
        lastPageSelected() {
            return this.selected === this.pageCount || this.pageCount === 0;
        },
        selectFirstPage() {
            if (this.selected <= 1) return;

            this.handlePageSelected(1);
        },
        selectLastPage() {
            if (this.selected >= this.pageCount) return;

            this.handlePageSelected(this.pageCount);
        },
        hrefValue(pageNum) {
            let tempStr = this.urlQueryVars.length > 0 ? `${this.urlQueryVars}&page-value=${pageNum}` : `?page-value=${pageNum}`;
            return `${this.currentBaseUrl}${tempStr}`;
        }
    },
    beforeMount() {
        this.innerValue = this.initialPage;
    },
    beforeUpdate() {
        if (this.forcePage === undefined) return;
        if (this.forcePage !== this.selected) {
            this.selected = this.forcePage;
        }
    },
};
</script>

<template>
    <ul :class="containerClass" v-if="!noLiSurround">
        <li
            v-if="firstLastButton"
            :class="[pageClass, firstPageSelected() ? disabledClass : '']"
        >
            <a
                @click.prevent="selectFirstPage()"
                @keyup.enter.prevent="selectFirstPage()"
                :class="pageLinkClass"
                :tabindex="firstPageSelected() ? -1 : 0"
                v-html="firstButtonText"
                :href="hrefValue(1)"
            ></a>
        </li>

        <li
            v-if="!firstPageSelected() && !hidePrevNext"
            :class="[prevClass, firstPageSelected() ? disabledClass : '']"
        >
            <a
                @click.prevent="prevPage()"
                @keyup.enter.prevent="prevPage()"
                :class="prevLinkClass"
                :tabindex="firstPageSelected() ? -1 : 0"
                v-html="prevText"
                :href="this.selected <= 1 ? '#' : hrefValue(this.selected - 1)"
            ></a>
        </li>

        <li
            v-for="page in pages"
            :key="page.index"
            :class="[
                pageClass,
                page.selected ? activeClass : '',
                page.disabled ? disabledClass : '',
                page.breakView ? breakViewClass : '',
            ]"
        >
            <div
                v-if="page.breakView"
                :class="[pageLinkClass, breakViewLinkClass]"
                tabindex="0"
            >
                <span><slot name="breakViewContent">{{ breakViewText }}</slot></span>
            </div>
            <div v-else-if="page.disabled" :class="pageLinkClass" tabindex="0">{{
                page.content
            }}</div>
            <a
                v-else
                @click.prevent="handlePageSelected(page.index + 1)"
                @keyup.enter.prevent="handlePageSelected(page.index + 1)"
                :class="pageLinkClass"
                tabindex="0"
                :href="hrefValue(page.index + 1)"
            >{{ page.content }}</a>
        </li>

        <li
            v-if="!lastPageSelected() && !hidePrevNext"
            :class="[nextClass, lastPageSelected() ? disabledClass : '']"
        >
            <a
                @click.prevent="nextPage()"
                @keyup.enter.prevent="nextPage()"
                :class="nextLinkClass"
                :tabindex="lastPageSelected() ? -1 : 0"
                v-html="nextText"
                :href="this.selected >= this.pageCount ? null : hrefValue(this.selected + 1)"
            ></a>
        </li>

        <li
            v-if="firstLastButton"
            :class="[pageClass, lastPageSelected() ? disabledClass : '']"
        >
            <a
                @click.prevent="selectLastPage()"
                @keyup.enter.prevent="selectLastPage()"
                :class="pageLinkClass"
                :tabindex="lastPageSelected() ? -1 : 0"
                v-html="lastButtonText"
                :href="this.selected >= this.pageCount ? null : hrefValue(this.pageCount)"
            ></a>
        </li>
    </ul>

    <div :class="containerClass" v-else>
        <a
            v-if="firstLastButton"
            @click.prevent="selectFirstPage()"
            @keyup.enter.prevent="selectFirstPage()"
            :class="[pageLinkClass, firstPageSelected() ? disabledClass : '']"
            tabindex="0"
            v-html="firstButtonText"
            :href="hrefValue(1)"
        ></a>
        <a
            v-if="!firstPageSelected() && !hidePrevNext"
            @click.prevent="prevPage()"
            @keyup.enter.prevent="prevPage()"
            :class="[prevLinkClass, firstPageSelected() ? disabledClass : '']"
            tabindex="0"
            v-html="prevText"
            :href="this.selected <= 1 ? '#' : hrefValue(this.selected - 1)"
        ></a>
        <template v-for="page in pages">
            <div
                v-if="page.breakView"
                :key="page.index"
                :class="[
                    pageLinkClass,
                    breakViewLinkClass,
                    page.disabled ? disabledClass : '',
                ]"
                tabindex="0"
            >
                <span><slot name="breakViewContent">{{ breakViewText }}</slot></span>
            </div>
            <div
                v-else-if="page.disabled"
                :key="page.index"
                :class="[
                    pageLinkClass,
                    page.selected ? activeClass : '',
                    disabledClass,
                ]"
                tabindex="0"
            >{{ page.content }}</div>
            <a
                v-else
                :key="page.index"
                @click.prevent="handlePageSelected(page.index + 1)"
                @keyup.enter.prevent="handlePageSelected(page.index + 1)"
                :class="[pageLinkClass, page.selected ? activeClass : '']"
                tabindex="0"
                :href="hrefValue(page.index + 1)"
            >{{ page.content }}</a>
        </template>
        <a
            v-if="!lastPageSelected() && !hidePrevNext"
            @click.prevent="nextPage()"
            @keyup.enter.prevent="nextPage()"
            :class="[nextLinkClass, lastPageSelected() ? disabledClass : '']"
            tabindex="0"
            v-html="nextText"
            :href="this.selected >= this.pageCount ? '#' : hrefValue(this.selected + 1)"
        ></a>
        <a
            v-if="firstLastButton"
            @click.prevent="selectLastPage()"
            @keyup.enter.prevent="selectLastPage()"
            :class="[pageLinkClass, lastPageSelected() ? disabledClass : '']"
            tabindex="0"
            v-html="lastButtonText"
            :href="this.selected >= this.pageCount ? '#' : hrefValue(this.pageCount)"
        ></a>
    </div>
</template>
